/**
 * Utility functions to capture metadata for signature fields
 *
 * NOTE ON LOCATION: this used to call navigator.geolocation and store the
 * signer's latitude/longitude. That was removed deliberately. It identified a
 * place, not a person, so it added nothing to attribution (the authenticated
 * account does that); it was client-supplied and therefore forgeable; it was
 * only present when a parent granted permission, so it was inconsistent as
 * evidence; and it was precise household location for families of minors.
 * It also caused a real failure: the permission prompt could leave the signature
 * save waiting forever, so a parent's signature was silently never recorded.
 * Do not reintroduce it — see the signature audit trail (server-stamped user,
 * IP, timestamp, and content hash) for what actually documents a signing.
 */

/**
 * Detect device type from user agent
 */
export const detectDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();

  if (/tablet|ipad|playbook|silk/i.test(ua)) {
    return 'Tablet';
  }

  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(ua)) {
    return 'Mobile';
  }

  if (/macintosh|mac os x/i.test(ua)) {
    return 'Desktop (Mac)';
  }

  if (/windows/i.test(ua)) {
    return 'Desktop (Windows)';
  }

  if (/linux/i.test(ua)) {
    return 'Desktop (Linux)';
  }

  return 'Desktop (Unknown)';
};

/**
 * Get client metadata (device type, screen, local timestamp).
 * Synchronous by design: nothing here may block saving a signature. The
 * authoritative IP address, signing user, and server timestamp are stamped
 * server-side at submit time.
 */
export const getClientMetadata = () => {
  return {
    deviceType: detectDeviceType(),
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString()
  };
};
